import { ref, computed } from 'vue';

export interface LoaderConfig {
  variant?: 'inline' | 'overlay' | 'fullscreen' | 'card';
  message?: string;
  subtitle?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  color?: string;
  showSpinner?: boolean;
  showLogo?: boolean;
  backdrop?: boolean;
  disabled?: boolean;
}

// Simple reactive singleton to track in-flight API requests
const pendingCount = ref(0);

// Optional debounce to avoid flicker on very fast requests
const isVisible = ref(false);
let showTimer: ReturnType<typeof setTimeout> | undefined;
let hideTimer: ReturnType<typeof setTimeout> | undefined;

// Default loader config for reference
const defaultConfig: LoaderConfig = {
  variant: 'overlay',
  message: '',
  subtitle: '',
  size: 'sm',
  color: 'hp-blue',
  showSpinner: true,
  showLogo: false,
  backdrop: false,
  disabled: false,
};
// Loader configuration state
const loaderConfig = ref<LoaderConfig>(defaultConfig);
// Track when a message was set to ensure minimum display time
const messageSetTime = ref<number | null>(null);
let resetTimer: ReturnType<typeof setTimeout> | undefined;
const MIN_MESSAGE_DISPLAY_TIME = 2000;

const updateVisibility = () => {
  if (pendingCount.value > 0) {
    // Delay show slightly
    if (hideTimer) {
      clearTimeout(hideTimer);
      hideTimer = undefined;
    }
    if (!showTimer) {
      showTimer = globalThis.setTimeout(() => {
        requestAnimationFrame(() => {
          isVisible.value = true;
        });
        showTimer = undefined;
      }, 150);
    }
  } else {
    // Delay hide slightly to prevent flashing between quick sequential calls
    if (showTimer) {
      globalThis.clearTimeout(showTimer);
      showTimer = undefined;
    }
    if (!hideTimer) {
      hideTimer = globalThis.setTimeout(() => {
        requestAnimationFrame(() => {
          isVisible.value = false;
        });
        hideTimer = undefined;
      }, 200);
    }
  }
};

export const startRequest = (): void => {
  pendingCount.value += 1;
  updateVisibility();
};

export const endRequest = (): void => {
  if (pendingCount.value > 0) pendingCount.value -= 1;
  updateVisibility();
};

export const setLoaderConfig = (config: Partial<LoaderConfig>): void => {
  // Cancel any pending reset when setting new config
  if (resetTimer) {
    clearTimeout(resetTimer);
    resetTimer = undefined;
  }

  loaderConfig.value = { ...defaultConfig, ...config };
  // Track when a message is set for minimum display time
  if (config.message || config.subtitle) {
    messageSetTime.value = Date.now();
  } else {
    messageSetTime.value = null;
  }
};

const resetLoaderConfig = (immediate = false): void => {
  // Cancel any pending reset timer
  if (resetTimer) {
    clearTimeout(resetTimer);
    resetTimer = undefined;
  }

  const now = Date.now();
  const hasMessage = loaderConfig.value.message || loaderConfig.value.subtitle;
  const messageAge = messageSetTime.value ? now - messageSetTime.value : 0;

  // If there's a message and it hasn't been shown for minimum time, delay the reset
  if (!immediate && hasMessage && messageAge < MIN_MESSAGE_DISPLAY_TIME) {
    const delay = MIN_MESSAGE_DISPLAY_TIME - messageAge;
    resetTimer = globalThis.setTimeout(() => {
      requestAnimationFrame(() => {
        loaderConfig.value = { ...defaultConfig };
        messageSetTime.value = null;
        resetTimer = undefined;
      });
    }, delay);
  } else {
    loaderConfig.value = { ...defaultConfig };
    messageSetTime.value = null;
  }
};

export const getResetLoaderConfig = () => {
  return resetLoaderConfig;
};

export const isGlobalLoading = computed(() => isVisible.value);

export const loaderVariant = computed(() => loaderConfig.value.variant ?? 'overlay');
export const loaderMessage = computed(() => loaderConfig.value.message ?? '');
export const loaderSubtitle = computed(() => loaderConfig.value.subtitle ?? '');
export const loaderSize = computed(() => loaderConfig.value.size ?? 'sm');
export const loaderColor = computed(() => loaderConfig.value.color ?? 'hp-blue');
export const loaderShowSpinner = computed(() => loaderConfig.value.showSpinner ?? true);
export const loaderShowLogo = computed(() => loaderConfig.value.showLogo ?? false);
export const loaderBackdrop = computed(() => loaderConfig.value.backdrop ?? false);
export const loaderDisabled = computed(() => loaderConfig.value.disabled ?? false);
